import { useCallback, useState } from 'react';
import type {
  AiVerificationAnswerValue,
  AiVerificationQuestion,
  AiVerificationSnapshot,
} from '@citizen-walk/shared-types';
import { enqueueAction } from '../../lib/actionOutbox';

/**
 * 検証回答 (AiVerification) を submit / update する hook (Sprint V)。
 *
 * Path: `/districts/{districtId}/posts/{postId}/verifications/{uid}`
 * firestore.rules で:
 *   - doc ID = uid 必須
 *   - participantUid = auth.uid 必須 (なりすまし防止)
 *   - canAccessDistrict(districtId) で地区メンバーシップ確認
 *
 * `setDoc` は merge:false (上書き)、新規時 submittedAt = serverTimestamp、
 * 更新時 submittedAt は既存値を維持、updatedAt は常に serverTimestamp。
 *
 * `completed` フラグは required 質問が全て埋まっているか + freeComment 要件で算出。
 *
 * **Phase 2 層1 (docs/03 §7.5-3、2026-06-07)**: 書込を SDK 直接 setDoc から
 * **リース非依存・冪等な action outbox 経由** (`lib/actionOutbox.ts`、kind='verification') に変更。
 *   - submit は server ack を待たず即 enqueue するので **送信ハングが無い**。
 *   - doc ID = uid 固定で再送は同一 doc 上書き = 冪等 (clientId = 送信先 doc path で coalesce)。
 *   - 実送信は `useActionOutbox` (AppHome/MapPage mount) が online 時に flush。survey と同じ
 *     **タイムスタンプ方針データ駆動** (updatedAt=常に serverTimestamp / submittedAt=既存温存) を flush 側で適用。
 *   - 撤回 (`withdrawMyVerifications`) との競合は、撤回成功後に該当地区の未送信 verification entry を
 *     outbox から除去して回避する (`useMyVerificationCount.withdraw`)。
 */

export interface SubmitVerificationInput {
  /** free verification の場合は null */
  assignmentSetId: string | null;
  /** 検証対象投稿の投稿者 UID (投稿 doc から写す)。旧 doc で不明なら null。 */
  postAuthorUid: string | null;
  /**
   * 検証者本人 (端末引き継ぎ元 UID を含む) の投稿かどうか (client 計算値)。
   * 集計 trigger は post.authorUid と検証者の selfUids から独立に判定する。
   */
  isOwnPost: boolean;
  templateId: string;
  templateVersion: number;
  aiSnapshot: AiVerificationSnapshot;
  perPostAnswers: Record<string, AiVerificationAnswerValue>;
  /** rank (1-based, 数値の文字列) → questionId → 値 */
  perClassificationAnswers: Record<string, Record<string, AiVerificationAnswerValue>>;
  freeComment: string | null;
  /**
   * completed 計算用に template の required 質問群を渡す。
   * (build payload 関数で計算するため、submit 関数の引数として受け取る)
   */
  perPostQuestions: AiVerificationQuestion[];
  perClassificationQuestions: AiVerificationQuestion[];
  /** perClassificationQuestions の「適用 rank」を計算するための実際の rank 数 (post.classifications.length) */
  totalRanks: number;
  /** 既存 verification の submittedAt (ISO)、新規時は null */
  existingSubmittedAt: string | null;
}

export type SubmitState =
  | { status: 'idle'; error: null }
  | { status: 'submitting'; error: null }
  | { status: 'success'; error: null }
  | { status: 'error'; error: Error };

export interface UseSubmitVerificationResult {
  submit: (input: SubmitVerificationInput) => Promise<void>;
  state: SubmitState;
}

/** rank 適用判定 (shared-types の ranksForQuestion と同等)。 */
function ranksForQuestionLocal(q: AiVerificationQuestion, totalRanks: number): number[] {
  const target = q.appliesToRanks ?? 'top';
  if (target === 'all') return Array.from({ length: totalRanks }, (_, i) => i + 1);
  if (target === 'top') return totalRanks > 0 ? [1] : [];
  if (target === 'top3') return Array.from({ length: Math.min(3, totalRanks) }, (_, i) => i + 1);
  return target.filter((r) => r >= 1 && r <= totalRanks);
}

/**
 * required 質問が全て埋まっているか判定 (pure)。
 * - perPostQuestions: enabled かつ required な質問は perPostAnswers に entry が必要
 * - perClassificationQuestions: enabled かつ required な質問は、適用 rank ごとに
 *   perClassificationAnswers[rank][qid] に entry が必要
 *
 * test しやすいよう export。
 */
export function computeCompleted(args: {
  perPostQuestions: AiVerificationQuestion[];
  perClassificationQuestions: AiVerificationQuestion[];
  totalRanks: number;
  perPostAnswers: Record<string, AiVerificationAnswerValue>;
  perClassificationAnswers: Record<string, Record<string, AiVerificationAnswerValue>>;
}): boolean {
  for (const q of args.perPostQuestions) {
    if (!q.enabled || !q.required) continue;
    if (!(q.id in args.perPostAnswers)) return false;
  }
  for (const q of args.perClassificationQuestions) {
    if (!q.enabled || !q.required) continue;
    const ranks = ranksForQuestionLocal(q, args.totalRanks);
    for (const r of ranks) {
      const rankAnswers = args.perClassificationAnswers[String(r)];
      if (!rankAnswers || !(q.id in rankAnswers)) return false;
    }
  }
  return true;
}

/**
 * verification doc の **タイムスタンプ以外**の確定フィールド (純粋関数、outbox payload.body 用)。
 * `completed` は送信時点で算出して焼き込む。outbox は serverTimestamp sentinel を保存できないため
 * body と timestamp 方針を分離する。
 */
export function buildVerificationBody(args: {
  uid: string;
  input: SubmitVerificationInput;
}): Record<string, unknown> {
  const completed = computeCompleted({
    perPostQuestions: args.input.perPostQuestions,
    perClassificationQuestions: args.input.perClassificationQuestions,
    totalRanks: args.input.totalRanks,
    perPostAnswers: args.input.perPostAnswers,
    perClassificationAnswers: args.input.perClassificationAnswers,
  });
  return {
    participantUid: args.uid,
    assignmentSetId: args.input.assignmentSetId,
    postAuthorUid: args.input.postAuthorUid,
    isOwnPost: args.input.isOwnPost,
    templateId: args.input.templateId,
    templateVersion: args.input.templateVersion,
    aiSnapshot: args.input.aiSnapshot,
    perPostAnswers: args.input.perPostAnswers,
    perClassificationAnswers: args.input.perClassificationAnswers,
    freeComment: args.input.freeComment,
    completed,
  };
}

/**
 * setDoc payload を組立 (test しやすいよう export、pure)。outbox 化後は flush 側 (useActionOutbox の
 * timestamp 方針適用) が同等 payload を組むため、本関数は単体テスト + 参照として残す
 * (body 部は `buildVerificationBody` に集約)。
 */
export function buildVerificationPayload(args: {
  uid: string;
  input: SubmitVerificationInput;
  serverTimestampSentinel: unknown;
}): Record<string, unknown> {
  return {
    ...buildVerificationBody(args),
    // 新規時 serverTimestamp、既存は ISO 文字列を温存。
    submittedAt: args.input.existingSubmittedAt ?? args.serverTimestampSentinel,
    updatedAt: args.serverTimestampSentinel,
  };
}

export function useSubmitVerification(
  districtId: string,
  postId: string,
  uid: string,
): UseSubmitVerificationResult {
  const [state, setState] = useState<SubmitState>({ status: 'idle', error: null });

  const submit = useCallback(
    async (input: SubmitVerificationInput): Promise<void> => {
      setState({ status: 'submitting', error: null });
      try {
        // リース非依存・冪等な outbox へ enqueue (clientId = 送信先 doc path で coalesce)。
        // submittedAt 温存は flush 時に getDoc で行うため、ここで既存を読む必要はない。
        await enqueueAction({
          clientId: `districts/${districtId}/posts/${postId}/verifications/${uid}`,
          kind: 'verification',
          op: 'set',
          districtId,
          payload: {
            body: buildVerificationBody({ uid, input }),
            serverTimestampFields: ['updatedAt'],
            createOnlyTimestampFields: ['submittedAt'],
          },
        });
        setState({ status: 'success', error: null });
      } catch (e) {
        setState({ status: 'error', error: e as Error });
        throw e;
      }
    },
    [districtId, postId, uid],
  );

  return { submit, state };
}
